import React, { useEffect, useState } from 'react';
import { useGetPsiProfile, useUpdatePsiProfile } from '@/lib/api/psi-profile.ts';
import { Stack, Typography, Box, Button } from '@mui/material';
import PsiConfigFields, { PsiConfigForm } from './PsiConfigFields.tsx';
import { enqueueSnackbar } from 'notistack';
import { StackedSnackbar } from '@/components/ui/StackedSnackbar.tsx';

const PsiConfigSectionForm: React.FC = () => {
  const { data, isLoading } = useGetPsiProfile();
  const { mutate: updatePsiProfile, isPending } = useUpdatePsiProfile();
  const [psiConfig, setPsiConfig] = useState<PsiConfigForm>({
    volume: null,
    totalClosedDeals: '',
    specialistInvestmentTypes: [],
    historicalAvgReturn: null,
    averageTicket: null,
    coveredProvinces: [],
    companyName: '',
    pricingModel: 'PACKAGE',
    feeAmount: null,
    feeReturnAmount: null,
    serviceConfigs: [],
    payoutInfo: { iban: '', bankName: '', accountHolderName: '' },
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (data) {
      setPsiConfig({
        volume: data.volume ?? null,
        totalClosedDeals: data.totalClosedDeals?.toString() ?? '',
        specialistInvestmentTypes: data.specialistInvestmentTypes ?? [],
        historicalAvgReturn: data.historicalAvgReturn ?? null,
        averageTicket: data.averageTicket ?? null,
        coveredProvinces: data.preferredProvinces ?? [],
        companyName: data.companyName ?? '',
        pricingModel: data.pricingModel ?? 'PACKAGE',
        serviceConfigs: data.serviceConfigs ?? [],
        feeAmount: data.feeAmount ?? null,
        feeReturnAmount: data.feeReturnAmount ?? null,
        payoutInfo: {
          iban: data.payoutInfo?.iban ?? '',
          bankName: data.payoutInfo?.bankName ?? '',
          accountHolderName: data.payoutInfo?.accountHolderName ?? '',
        },
      });
    }
  }, [data]);

  const isPsiValid =
    !!psiConfig.volume &&
    !!psiConfig.totalClosedDeals &&
    psiConfig.specialistInvestmentTypes.length > 0 &&
    !!psiConfig.historicalAvgReturn &&
    !!psiConfig.averageTicket &&
    psiConfig.feeReturnAmount !== null &&
    (psiConfig.pricingModel === 'PACKAGE'
      ? !!psiConfig.feeAmount
      : psiConfig.serviceConfigs.length > 0 &&
        psiConfig.serviceConfigs.every(
          (config) => !!config.service && (!!config.fee || !!config.percentage)
        ));

  const handleSave = () => {
    setError('');
    updatePsiProfile(
      {
        volume: Number(psiConfig.volume),
        totalClosedDeals: Number(psiConfig.totalClosedDeals),
        specialistInvestmentTypes: psiConfig.specialistInvestmentTypes,
        historicalAvgReturn: Number(psiConfig.historicalAvgReturn),
        averageTicket: Number(psiConfig.averageTicket),
        preferredProvinces: psiConfig.coveredProvinces,
        companyName: psiConfig.companyName,
        serviceConfigs: psiConfig.serviceConfigs,
        pricingModel: psiConfig.pricingModel,
        feeAmount: Number(psiConfig.feeAmount),
        feeReturnAmount: Number(psiConfig.feeReturnAmount),
        payoutInfo: psiConfig.payoutInfo,
      },
      {
        onSuccess: () =>
          enqueueSnackbar(<StackedSnackbar title="Configuración de PSI actualizada" />, {
            variant: 'success',
          }),
        onError: () => setError('Error al guardar los cambios'),
      }
    );
  };

  if (isLoading) return <Typography>Cargando configuración PSI...</Typography>;

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} mb={2}>
        Parámetros de configuración PSI
      </Typography>
      <Stack spacing={2}>
        <PsiConfigFields value={psiConfig} onChange={setPsiConfig} />
        <Box display="flex" gap={2} alignItems="center">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={!isPsiValid || isPending}
          >
            Guardar cambios
          </Button>
          {error && <Typography color="error.main">{error}</Typography>}
        </Box>
      </Stack>
    </Box>
  );
};

export default PsiConfigSectionForm;
